import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, RefreshCw } from 'lucide-react';
import { myDocumentApi, MyDocumentRow } from '../services/api';
import { formatCompanyDate } from '../utils/companyDateFormat';

const MyDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MyDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const data = await myDocumentApi.list();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const outstanding = useMemo(() => rows.filter((r) => r.outstanding), [rows]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">My Documents</h1>
          <p className="text-sm text-neutral-500 mt-1">Read company policies and confirm you have understood them.</p>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-1 text-sm text-neutral-600">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {outstanding.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="font-medium text-amber-900 mb-2">Documents requiring attention</div>
          <div className="space-y-2">
            {outstanding.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => navigate(`/my-documents/${row.id}`)}
                className="w-full rounded-lg bg-white px-4 py-3 text-left border border-amber-100 hover:border-amber-300"
              >
                <div className="font-medium text-neutral-900">{row.name}</div>
                <div className="text-sm text-neutral-500">{row.categoryLabel} · Version {row.versionLabel} · Acknowledgement required</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">
            <FileText className="mx-auto mb-2 text-neutral-300" />
            No documents have been assigned to you yet.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-600">
              <tr>
                <th className="px-4 py-3 font-medium">Document</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Version</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Distributed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => navigate(`/my-documents/${row.id}`)}>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3">{row.categoryLabel}</td>
                  <td className="px-4 py-3">v{row.versionLabel}</td>
                  <td className="px-4 py-3">{statusLabel(row)}</td>
                  <td className="px-4 py-3">{formatCompanyDate(row.distributedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

function statusLabel(row: MyDocumentRow): string {
  if (row.status === 'Acknowledged') return 'Acknowledged';
  if (row.status === 'CoveredByPriorVersion') return 'Acknowledged (prior version)';
  if (row.status === 'Opened') return 'Opened — acknowledgement outstanding';
  if (row.status === 'NotRequired') return 'No acknowledgement required';
  if (row.outstanding) return 'Acknowledgement required';
  return row.status;
}

export default MyDocuments;
